import React from 'react';
import { BarChart, TrendingUp, Calendar } from 'lucide-react';
import { getTranslation } from '../utils/translations';
import { getIconColor, getProgressBarColor, getTextColor } from '../utils/themeUtils';
import type { Language, DailyPrayers, Theme } from '../types';

interface StatisticsProps {
  completedCount: number;
  totalCount: number;
  isDark: boolean;
  language: Language;
  prayerHistory: DailyPrayers[];
  selectedDate: Date;
  isToday: boolean;
  theme: Theme;
}

export function Statistics({ 
  completedCount, 
  totalCount, 
  isDark, 
  language, 
  prayerHistory, 
  selectedDate,
  isToday,
  theme 
}: StatisticsProps) {
  const percentage = Math.round((completedCount / totalCount) * 100);

  // Calculate weekly average
  const getWeeklyAverage = () => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    const weeklyData = prayerHistory.filter(day => {
      const dayDate = new Date(day.date);
      return dayDate >= oneWeekAgo && dayDate <= new Date();
    });

    if (weeklyData.length === 0) return 0;

    const totalCompleted = weeklyData.reduce((sum, day) => {
      return sum + day.prayers.filter(p => p.completed).length;
    }, 0);

    return Math.round((totalCompleted / (weeklyData.length * 5)) * 100);
  };

  // Calculate monthly average
  const getMonthlyAverage = () => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    
    const monthlyData = prayerHistory.filter(day => {
      const dayDate = new Date(day.date);
      return dayDate >= oneMonthAgo && dayDate <= new Date();
    });

    if (monthlyData.length === 0) return 0;

    const totalCompleted = monthlyData.reduce((sum, day) => {
      return sum + day.prayers.filter(p => p.completed).length;
    }, 0);

    return Math.round((totalCompleted / (monthlyData.length * 5)) * 100);
  };

  const weeklyAvg = getWeeklyAverage();
  const monthlyAvg = getMonthlyAverage();

  const formatDateForTitle = (date: Date) => {
    return date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className={`rounded-xl shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'} p-4 sm:p-6`}>
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <BarChart className="w-5 h-5" style={getIconColor(theme)} />
        <h2 className="text-base sm:text-lg font-semibold">
          {isToday 
            ? getTranslation('stats.todayProgress', language)
            : `${getTranslation('stats.progressFor', language)} ${formatDateForTitle(selectedDate)}`
          }
        </h2>
      </div>
      
      <div className="space-y-4 sm:space-y-6">
        {/* Main progress bar */}
        <div>
          <div className="relative h-3 sm:h-4 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="absolute h-full transition-all duration-300"
              style={{
                width: `${percentage}%`,
                ...getProgressBarColor(theme)
              }}
            />
          </div>
          
          <div className="mt-2 text-center">
            <span className="font-medium text-lg sm:text-xl">{completedCount}</span>
            <span className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-500'}`}> of </span>
            <span className="font-medium text-lg sm:text-xl">{totalCount}</span>
            <span className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-500'}`}> prayers completed (</span>
            <span className="font-medium text-lg sm:text-xl" style={getTextColor(theme)}>{percentage}%</span>
            <span className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>)</span>
          </div>
        </div>

        {/* Additional statistics for today only */}
        {isToday && prayerHistory.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className={`p-3 sm:p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4" style={getIconColor(theme)} />
                <span className="text-sm font-medium">{getTranslation('stats.weeklyAverage', language)}</span>
              </div>
              <div className="text-xl sm:text-2xl font-bold" style={getTextColor(theme)}>{weeklyAvg}%</div>
            </div>

            <div className={`p-3 sm:p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4" style={getIconColor(theme)} />
                <span className="text-sm font-medium">{getTranslation('stats.monthlyAverage', language)}</span>
              </div>
              <div className="text-xl sm:text-2xl font-bold" style={getTextColor(theme)}>{monthlyAvg}%</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}